import axios from 'axios';
import { decode } from 'jsonwebtoken';
import { httpError, rtnError } from './error';
import { clearStorage, getStorage, setStorage } from '@/utils/storage';
import router from '@/plugins/router';

interface JwtToken {
  sub: string;
  exp: number;
  iat: number;
  data: string;
  client: string;
}

// 最近一次请求的 token
let latestToken = getStorage('token');

// 创建 http 实例(用来发送 http 请求)
export const http = axios.create({
  baseURL: '/api',
  timeout: 1000 * 60 * 60 * 2,  // 不限制过期时间
});

// 请求拦截器
http.interceptors.request.use((config) => {
  // 添加 token
  config.headers.Authorization = getStorage('token');

  // 添加时间戳
  if (config.method === 'get') {
    const timestamp = `t=${Date.now()}`;
    const caret = /\?/.test(config.url as string) ? '&' : '?';
    config.url = config.url + caret + timestamp;
  }

  return config;
});

// 响应拦截器
http.interceptors.response.use((config) => {
  const res = config.data as { rtnCode: number, data: any, message: string };
  const rtnCode = res.rtnCode;
  const token = config.headers.authorization;
  const ignoreCodes = config.config.ignoreCode || [];

  // 将 token 信息存入 localstorage (仅当 token 发生变更时才存储)
  if (token && token !== latestToken) {
    const tokenStr = token.substring('Bearer'.length);
    const tokenInfo = decode(tokenStr) as JwtToken;
    const userId = JSON.parse(tokenInfo.data || '{}').id;
    latestToken = token;

    // 匿名用户
    if (!userId) {
      clearStorage();
      rtnError(-1000, '当前用户无法正常使用');
      router.push({ name: 'login' });
      return null;  // 之所以不用 reject() 是防止请求错误和当前错误信息一起出现
    }

    // 正常用户
    setStorage('token', token);
    setStorage('expire', tokenInfo.exp * 1000);
    setStorage('userInfo', tokenInfo.data);
  }

  // 如果业务错误码是被忽略的，则会正常返回(即使有业务错误，依然会进入 then 分支)
  if (rtnCode && ignoreCodes.indexOf(rtnCode) !== -1) {
    return res;
  }

  // 拦截业务异常
  if (rtnCode !== 0) {
    rtnError(rtnCode, res.message);
    return Promise.reject(res);
  }

  return res.data;
}, (error) => {
  // 如果发送请求时，传入了 ignoreCode 参数，则对应的 http 错误，不会在页面上提示
  const httpCode = error?.response?.status || -1;
  const ignoreCodes = error.config.ignoreCode || [];
  if (ignoreCodes.indexOf(httpCode) === -1) {
    httpError(error);
  }
  return Promise.reject(error);
});
